/**
 * Shared SSE streaming client for agent chat
 *
 * Single source of truth for Desktop Canvas, Mobile Canvas, and any
 * future consumers. Handles SSE parsing, abort control, and the full
 * event vocabulary: text, tool_call, tool_result, agent, complete, error.
 *
 * @module utils/streamAgentChat
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Stream an agent chat response via SSE.
 *
 * @param {Object}   opts
 * @param {string}   opts.message       - User message
 * @param {string}   opts.agentId       - Agent ID to route to
 * @param {string}   opts.sport         - Sport context
 * @param {string}   [opts.sessionId]   - Existing session ID
 * @param {Array}    [opts.attachments] - File/video attachments
 * @param {Function} [opts.onText]      - (chunk, fullText) => void
 * @param {Function} [opts.onToolCall]  - ({ tool, args }) => void
 * @param {Function} [opts.onToolResult]- ({ tool, success, mcpApp, toolResult }) => void
 * @param {Function} [opts.onAgent]     - ({ agent, agentId }) => void
 * @param {Function} [opts.onDone]      - (sessionId) => void
 * @param {Function} [opts.onError]     - (errorMessage) => void
 * @returns {AbortController} - Call .abort() to cancel the stream
 */
export default async function streamAgentChat({
  message,
  agentId,
  sport,
  sessionId,
  attachments,
  onText,
  onToolCall,
  onToolResult,
  onAgent,
  onDone,
  onError,
}) {
  const abortController = new AbortController();

  try {
    const res = await fetch(`${API_BASE}/api/agent/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      },
      body: JSON.stringify({
        message,
        agentId,
        properties: { sport, sportType: sport },
        ...(attachments?.length ? { attachments } : {}),
      }),
      signal: abortController.signal,
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => res.statusText);
      onError?.(errText || `HTTP ${res.status}`);
      return abortController;
    }

    const returnedSessionId = res.headers.get('X-Session-Id') || sessionId;
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';
    let gotComplete = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      let currentEvent = null;
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ') && currentEvent) {
          try {
            const data = JSON.parse(line.slice(6));

            switch (currentEvent) {
              case 'text':
                fullText += data.content || '';
                onText?.(data.content || '', fullText);
                break;
              case 'tool_call':
                onToolCall?.({ tool: data.tool, args: data.args });
                break;
              case 'tool_result':
                onToolResult?.({
                  tool: data.tool,
                  success: data.success,
                  mcpApp: data.mcpApp || null,
                  toolResult: data.toolResult || null,
                });
                break;
              case 'agent':
                onAgent?.({ agent: data.agent, agentId: data.agentId });
                break;
              case 'complete':
                gotComplete = true;
                onDone?.(returnedSessionId);
                break;
              case 'error':
                onError?.(data.error || 'Unknown error');
                break;
              default:
                break;
            }
          } catch {
            // skip malformed JSON
          }
          currentEvent = null;
        } else if (line === '') {
          currentEvent = null;
        }
      }
    }

    // Only fire onDone if stream ended without a 'complete' event (connection drop)
    if (!gotComplete) {
      onDone?.(returnedSessionId);
    }
  } catch (err) {
    if (err.name !== 'AbortError') {
      onError?.(err.message);
    }
  }

  return abortController;
}
